<script lang="ts">
  import { ProductLineEntity } from "@shared/services/event_tracking/entities";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import DownloadSection from "./DownloadSection.svelte";

  import type { ProductLine, Product } from "@shared/types/ProductLine";
  import type { SfProduct, VendorProduct } from "@shared/types/SevenFifty";
  import type { Distributor } from "@shared/types/Distributor";

  export let productLine: ProductLine;
  export let distributor: Distributor;
  export let selectedVintageProduct: Product;
  export let sevenFiftyProduct: SfProduct;
  export let vendorProduct: VendorProduct;
  export let mobileMode: boolean;

  let showDetails: boolean = false;
  let classes;
  let descriptionShown;
  let shouldShowMore;
  let fadeClasses;
  let tastingNotes;
  let distributorNotes = "";
  let pairsWell;
  let hasAll;

  const { trackClick } = EventTracking;
  const productLineEntity = new ProductLineEntity({ productLineId: productLine.id });

  const toggleDetails = () => {
    showDetails = !showDetails;

    const targetEvent = showDetails
      ? "show_more_on_product_description"
      : "show_less_on_product_description";
    trackClick(targetEvent, [productLineEntity]);
  };

  function pickDescription(productLine, selectedVintageProduct) {
    descriptionShown = selectedVintageProduct?.sf_description;
    if (!descriptionShown) {
      descriptionShown = productLine.description;
    }
    if (!descriptionShown) {
      descriptionShown = productLine.sf_description;
    }

    return descriptionShown;
  }

  const handleEnter = (e: KeyboardEvent) => {
    if (e.code === "Enter") {
      toggleDetails();
    }
  };

  $: {
    descriptionShown = pickDescription(productLine, selectedVintageProduct);
    pairsWell = productLine.pairs_well_with || selectedVintageProduct?.pairs_well_with;
    tastingNotes = productLine.tasting_notes || selectedVintageProduct?.tasting_notes;

    // we dont want to show the same description twice
    if (vendorProduct?.distributor_notes && descriptionShown !== vendorProduct.distributor_notes) {
      distributorNotes = vendorProduct?.distributor_notes_formatted || "";
    } else {
      distributorNotes = "";
    }

    hasAll = descriptionShown && (pairsWell || tastingNotes || distributorNotes);

    shouldShowMore =
      hasAll ||
      tastingNotes?.length > 275 ||
      pairsWell?.length > 275 ||
      descriptionShown?.length > 275 ||
      distributorNotes.length > 275;

    if (!showDetails && shouldShowMore && !mobileMode) {
      classes = "fizz-surface fizz-stack-4 details-closed";
      fadeClasses = "details-fade";
    } else {
      classes = "fizz-surface fizz-flow-16 fizz-stack-16";
      fadeClasses = "details-hidden";
    }
  }

  $: hasDownloads =
    sevenFiftyProduct?.shelf_talker ||
    sevenFiftyProduct?.supplier_tech_sheet ||
    vendorProduct?.shelf_talker ||
    vendorProduct?.distributor_tech_sheet;
  $: hasContent = descriptionShown || tastingNotes || pairsWell || distributorNotes || hasDownloads;
</script>

<style>
  .divider {
    height: 1px;
    border: none;
    background-color: #eaeaea;
  }
  .details-closed {
    overflow: hidden;
    position: relative;
    height: 130px;
  }
  .details-hidden {
    display: none;
  }

  .details-fade {
    position: absolute;
    bottom: 0px;
    display: block;
    width: 100%;
    height: 35%;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #ffffff 80%);
  }

  .distributor-notes :global(p) {
    margin-bottom: 10px;
  }
</style>

{#if hasContent}
  {#if mobileMode}
    <details
      class="fizz-accordion fizz-stack-16"
      id="description-accordion"
      on:click={toggleDetails}
      on:keyup={handleEnter}
    >
      <summary><h2 class="fizz-heading-4">Product information</h2></summary>
      <div class="fizz-inset-square-12">
        {#if descriptionShown}
          <div
            class="fizz-text-large"
            class:fizz-stack-16={tastingNotes || pairsWell || distributorNotes}
          >
            {descriptionShown}
          </div>
        {/if}
        {#if tastingNotes}
          <div class="fizz-stack-16">
            <h3 class="fizz-heading-4 fizz-stack-8">Tasting notes</h3>
            <div class="fizz-text-large">
              {tastingNotes}
            </div>
          </div>
        {/if}

        {#if pairsWell}
          <div class="fizz-stack-16">
            <h3 class="fizz-heading-4 fizz-stack-8">Pairs well with</h3>
            <div class="fizz-text-large">
              {pairsWell}
            </div>
          </div>
        {/if}
        {#if distributorNotes && distributor}
          <div class="fizz-stack-16">
            <h3 class="fizz-heading-4 fizz-stack-8">
              Notes from {distributor.name}
            </h3>
            <div class="fizz-text-large distributor-notes">
              {@html distributorNotes}
            </div>
          </div>
        {/if}
      </div>
      <DownloadSection
        distributorId={distributor?.id}
        productLineId={productLine.id}
        {selectedVintageProduct}
        {sevenFiftyProduct}
        {vendorProduct}
      />
    </details>
  {/if}
  {#if !mobileMode}
    <div class="fizz-flow-16 fizz-stack-16">
      <hr class="divider" />
      <div>
        <div class={classes}>
          {#if descriptionShown || distributorNotes}
            <h2 class="fizz-heading-3 fizz-stack-12">Product information</h2>
            {#if descriptionShown}
              <div class="fizz-text-large">
                {descriptionShown}
              </div>
            {/if}
          {/if}

          {#if tastingNotes && pairsWell}
            <div class="fizz-flex-row">
              {#if tastingNotes}
                <div class="fizz-half-width" style="margin-block-end: auto">
                  <h3 class="fizz-heading-4 fizz-stack-8">Tasting notes</h3>
                  <div class="fizz-text-large">
                    {tastingNotes}
                  </div>
                </div>
              {/if}
              {#if pairsWell}
                <div class="fizz-half-width" style="margin-block-end: auto">
                  <h3 class="fizz-heading-4 fizz-stack-8">Pairs well with</h3>
                  <div class="fizz-text-large">
                    {pairsWell}
                  </div>
                </div>
              {/if}
            </div>
          {/if}

          {#if tastingNotes && !pairsWell}
            <div>
              <h3 class="fizz-heading-4 fizz-stack-8">Tasting notes</h3>
              <div class="fizz-text-large">
                {tastingNotes}
              </div>
            </div>
          {/if}

          {#if pairsWell && !tastingNotes}
            <div>
              <h3 class="fizz-heading-4 fizz-stack-8">Pairs well with</h3>
              <div class="fizz-text-large">
                {pairsWell}
              </div>
            </div>
          {/if}

          {#if distributorNotes && distributor}
            <h3 class="fizz-heading-4 fizz-stack-8">Notes from {distributor.name}</h3>
            <div class="fizz-text-large distributor-notes">
              {@html distributorNotes}
            </div>
          {/if}
          {#if !showDetails && !mobileMode}
            <div class={fadeClasses} />
          {/if}
        </div>
        {#if shouldShowMore && !mobileMode}
          <a
            href={"#"}
            class="fizz-text-strong"
            style="cursor: pointer"
            on:click|preventDefault={toggleDetails}
          >
            {showDetails ? "SHOW LESS" : "SHOW MORE"}
          </a>
        {/if}
      </div>
      <DownloadSection
        distributorId={distributor?.id}
        productLineId={productLine.id}
        {selectedVintageProduct}
        {sevenFiftyProduct}
        {vendorProduct}
      />
    </div>
  {/if}
{/if}
